import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import type { LeadCardResponse, PhoneSlot } from "@gioia365/contracts";
import { Button } from "../../design/components/Button";
import { Icon } from "../../design/components/Icon";
import { Reason } from "../../design/components/Reason";
import { useT, type Translate } from "../../i18n/locale";
import type { TabContext } from "../../session/tab-context";
import { useDialerSessionIdFor } from "../../dialer/dialer-session";
import { useCallSession } from "../../telephony/call-session";
import { reasonText } from "../../telephony/call-texts";
import { formatPhoneForDisplay } from "../lead-display";
import "./call-desk.css";

// «Chiama» nella testata della scheda (piano M4, Task 13, mock-up `scheda-lead.html`, `call-pick`): chiama il primo
// telefono che si chiama, nell'ordine della scheda (il cellulare del titolare, poi il fisso, poi il cellulare del secondo
// titolare); con più telefoni il pulsante accanto apre «Quale numero chiamare», dove un telefono che non si chiama è spento
// con il suo motivo. Spento, «Chiama» dice sempre perché, con le parole del rifiuto del server (`callDesk.reasons.*`):
// durante una chiamata, o con l'esito di una chiamata ancora da registrare, il motivo è quello della chiamata. Sul Lead aperto
// di una sessione attiva del Power Dialer (piano M5, D5) la chiamata porta la sessione.

type Props = { tab: TabContext; card: LeadCardResponse; leadName: string };

type PhoneChoice = { slot: PhoneSlot; label: string; number: string; callable: boolean; reason: string | null };

/** Il nome del posto di un telefono, come nella scheda: «Cellulare», «Telefono fisso», «Cellulare di {secondo titolare}». */
export function phoneLabel(t: Translate, card: LeadCardResponse, slot: PhoneSlot): string {
  if (slot.holder === "primary") return t(slot.kind === "mobile" ? "leadCard.fields.mobile" : "leadCard.fields.landline");
  const second = card.lead.secondHolder;
  const name = [second?.firstName, second?.lastName].filter(Boolean).join(" ");
  return name ? t("callDesk.secondMobile", { name }) : t("leadCard.fields.secondHolderMobile");
}

/** I telefoni attivi nell'ordine della scheda, con la regola di ciascuno. */
function choicesOf(t: Translate, card: LeadCardResponse): PhoneChoice[] {
  const mine = card.calling.activeCall?.mine ?? false;
  return card.calling.phones.flatMap((rule) => {
    const phone = card.lead.phones.find((candidate) => candidate.id === rule.phoneId);
    if (!phone) return [];
    const slot: PhoneSlot = { holder: phone.holder, kind: phone.kind };
    return [
      {
        slot,
        label: phoneLabel(t, card, slot),
        number: formatPhoneForDisplay(phone.e164),
        callable: rule.callable,
        reason: rule.callable || rule.reason === null ? null : reasonText(t, rule.reason, rule.byDisplayName, mine),
      },
    ];
  });
}

export function CallButton({ tab, card, leadName }: Props) {
  const t = useT();
  const { session, start } = useCallSession();
  const dialerSessionId = useDialerSessionIdFor(tab, card.lead.id);
  const reasonId = useId();
  const menuId = useId();
  const [menuOpen, setMenuOpen] = useState(false);
  const pickRef = useRef<HTMLButtonElement>(null);
  const splitRef = useRef<HTMLSpanElement>(null);
  const choices = choicesOf(t, card);
  const first = choices.find((choice) => choice.callable);
  const busy = session.phase !== "idle" && session.phase !== "error";
  const { calling } = card;

  let reason: string | null = null;
  if (busy) reason = t(session.phase === "outcome" ? "callDesk.busyOutcome" : "callDesk.busyCalling");
  else if (!calling.callable && calling.reason !== null) reason = reasonText(t, calling.reason, calling.byDisplayName, calling.activeCall?.mine ?? false);
  const disabled = busy || !calling.callable || !first;

  // Un clic fuori dal menu lo chiude, come nel mock-up.
  useEffect(() => {
    if (!menuOpen) return;
    const close = (event: PointerEvent) => {
      if (!splitRef.current?.contains(event.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, [menuOpen]);

  // Aperto il menu, il focus va sul primo numero che si chiama.
  useEffect(() => {
    if (menuOpen) document.getElementById(menuId)?.querySelector<HTMLButtonElement>("[role=menuitem]:not(:disabled)")?.focus();
  }, [menuOpen, menuId]);

  function call(slot: PhoneSlot) {
    setMenuOpen(false);
    void start({ tab, leadId: card.lead.id, phone: slot, leadName, ...(dialerSessionId === undefined ? {} : { dialerSessionId }) });
  }

  function onMenuKey(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === "Escape") {
      event.preventDefault();
      setMenuOpen(false);
      pickRef.current?.focus();
      return;
    }
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    event.preventDefault();
    const items = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>("[role=menuitem]:not(:disabled)"));
    const index = items.findIndex((item) => item === document.activeElement);
    const next = event.key === "ArrowDown" ? (index + 1) % items.length : (index - 1 + items.length) % items.length;
    items[next]?.focus();
  }

  return (
    <>
      <span className="g-call-split" ref={splitRef}>
        <Button variant="primary" disabled={disabled} aria-describedby={reason ? reasonId : undefined} onClick={() => first && call(first.slot)}>
          <Icon name="phone" />
          {t("callDesk.call")}
        </Button>
        {choices.length > 1 ? (
          <Button
            ref={pickRef}
            variant="primary"
            className="g-call-split__pick"
            disabled={busy}
            aria-label={t("callDesk.pickLabel")}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-controls={menuOpen ? menuId : undefined}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <Icon name="down" />
          </Button>
        ) : null}
        {menuOpen ? (
          <div id={menuId} className="g-call-menu" role="menu" tabIndex={-1} aria-label={t("callDesk.pickMenu")} onKeyDown={onMenuKey}>
            {choices.map((choice) => (
              <button
                key={`${choice.slot.holder}:${choice.slot.kind}`}
                type="button"
                role="menuitem"
                className="g-call-menu__item"
                disabled={!choice.callable}
                onClick={() => call(choice.slot)}
              >
                <strong>{choice.label}</strong>
                <span>{choice.number}</span>
                {choice.reason ? <span className="g-call-menu__reason">{choice.reason}</span> : null}
              </button>
            ))}
          </div>
        ) : null}
      </span>
      {reason ? (
        <Reason tone="warning" id={reasonId} className="g-call-split__reason">
          {reason}
        </Reason>
      ) : null}
    </>
  );
}
